<script setup>
import GenericLoading from '../../common/GenericLoading.vue'
import FeatureCollectionPanel from '../../common/FeatureCollectionPanel.vue';
import GetFeatureInfoLayer from './getFeatureInfoLayer.js'
import { PARTICULAR_FEATURE_TYPES } from '@/plugins/utils/constantes.js'
import { authorizedModule } from '@/plugins/utils/Authorization.js';
import { ref, watch, inject, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { usePortAllData } from '@/stores/portAllData'
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n'
import { clamp } from '@/plugins/utils/helpers.js'
const { t } = useI18n()

const PANEL_LEFT_CSS_VARIABLE_NAME = '--get-feature-info-left'
const PANEL_BOTTOM_CSS_VARIABLE_NAME = '--get-feature-info-bottom'
const PANEL_HEIGHT_CSS_VARIABLE_NAME = '--get-feature-info-height'

const $map = inject('$map')
const store = usePortAllData()

const { getFeatureInfo, getAuthConfig, getSimplifyGetFeatureInfo, getLayerColor, getIsMobile } = storeToRefs(store);
const { setFeatureInfo } = store

const gfiLayer = ref(null)

const pcBar = ref()
const resizeButton = ref()
const getFeatureInfoElmt = ref()
const featureCollectionPanel = ref()

const isLoading = ref(false)

const prevEvent = ref(null)
const moving = ref(false)
const resizing = ref(false)
const showInfos = ref(false)

const leftPosition = ref(0)
const bottomPosition = ref(0)
const height = ref(0)

const featureCollection = ref(null)
const selectedFeature = ref(null)

const notifRight = ref(false)

onMounted(() => {
  notifRight.value = authorizedModule('notification', getAuthConfig.value.authorizedModules)
  
  gfiLayer.value = new GetFeatureInfoLayer($map)

  pcBar.value.addEventListener('mousedown', featureInfoStartMoveHandler, { passive: true })
  pcBar.value.addEventListener('touchstart', featureInfoStartMoveHandler, { passive: true })
  document.body.addEventListener('mouseup', featureInfoEndMoveHandler, { passive: true })
  document.body.addEventListener('touchend', featureInfoEndMoveHandler, { passive: true })
  resizeButton.value.addEventListener('mousedown', featureInfoStartResizeHandler, { passive: true })
  resizeButton.value.addEventListener('touchstart', featureInfoStartResizeHandler, { passive: true })
  document.body.addEventListener('mouseup', featureInfoEndResizeHander, { passive: true })
  document.body.addEventListener('touchend', featureInfoEndResizeHander, { passive: true })

  const fi = getFeatureInfo.value
  if (fi && Object.entries(fi).length) {
    leftPosition.value = fi.left ? Number(fi.left.replace('px', '')) : 0
    bottomPosition.value = fi.bottom ? Number(fi.bottom.replace('px', '')) : 0
    height.value = fi.height ? Number(fi.height.replace('px', '')) : 0
  }

  document.addEventListener('getFeaturesInfo', async (e) => {
    const params = e.detail
    let layers = params.layers
    if (getSimplifyGetFeatureInfo.value) {
      const initialLayers = $map.getInitialLayerGroup();
      const groupLayers = initialLayers.getLayerByTag(['Work']);
      layers = groupLayers.map(l => l.getName())
    }
    await getFeatures(params.coord, layers, params.selectId, params.menu, true)
  })

  document.addEventListener('closeFI', closeFeatureInfoEvent, { passive: true })
})

onBeforeUnmount(() => {
  pcBar.value.removeEventListener('mousedown', featureInfoStartMoveHandler, { passive: true })
  pcBar.value.removeEventListener('touchstart', featureInfoStartMoveHandler, { passive: true })
  document.body.removeEventListener('mouseup', featureInfoEndMoveHandler, { passive: true })
  document.body.removeEventListener('touchend', featureInfoEndMoveHandler, { passive: true })
  resizeButton.value.removeEventListener('mousedown', featureInfoStartResizeHandler, { passive: true })
  resizeButton.value.removeEventListener('touchstart', featureInfoStartResizeHandler, { passive: true })
  document.body.removeEventListener('mouseup', featureInfoEndResizeHander, { passive: true })
  document.body.removeEventListener('touchend', featureInfoEndResizeHander, { passive: true })

  document.removeEventListener('closeFI', closeFeatureInfoEvent, { passive: true })
})

const closeFeatureInfo = () => {
  if (getIsMobile.value) showInfos.value = false
}

const closeFeatureInfoEvent = () => {
  showInfos.value = false
}

const closeInfos = () => {
  display(false)
}

const clear = () => {
  gfiLayer.value.clear()
  featureCollection.value = null
  selectedFeature.value = null
}

const featureInfoMoveHandler = (e) => {
  const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
  let x = leftPosition.value
  let y = bottomPosition.value
  if (e instanceof MouseEvent) {
    x += (e.screenX - prevEvent.value.screenX)
    y -= (e.screenY - prevEvent.value.screenY)
  } else if (e instanceof TouchEvent) {
    x += (e.changedTouches[0].screenX - prevEvent.value.changedTouches[0].screenX)
    y -= (e.changedTouches[0].screenY - prevEvent.value.changedTouches[0].screenY)
  }
  leftPosition.value = clamp(Math.round(x), 0, vw - 250)
  bottomPosition.value = clamp(Math.round(y), 0, vh - height.value)
  prevEvent.value = e
}

const featureInfoStartMoveHandler = (e)  => {
  if (e.target !== resizeButton.value) {
    prevEvent.value = e
    moving.value = true
    if (e instanceof MouseEvent) {
      document.body.addEventListener('mousemove', featureInfoMoveHandler, { passive: true })
    } else if (e instanceof TouchEvent) {
      document.body.addEventListener('touchmove', featureInfoMoveHandler, { passive: true })
    }
  }
}

const featureInfoEndMoveHandler = (e) => {
  if (moving.value) {
    if (e instanceof MouseEvent) {
      document.body.removeEventListener('mousemove', featureInfoMoveHandler, { passive: true })
    } else if (e instanceof TouchEvent) {
      document.body.removeEventListener('touchmove', featureInfoMoveHandler, { passive: true })
    }
    setFeaturePosition()
  }
}

const featureInfoResizeHandler = (e) => {
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
  let h = height.value
  if (e instanceof MouseEvent) h -= (e.screenY - prevEvent.value.screenY)
  else if (e instanceof TouchEvent) h -= (e.changedTouches[0].screenY - prevEvent.value.changedTouches[0].screenY)
  height.value = clamp(Math.round(h), 200, vh - bottomPosition.value)
  prevEvent.value = e
}

const featureInfoStartResizeHandler = (e) => {
  prevEvent.value = e
  resizing.value = true
  if (e instanceof MouseEvent) {
    document.body.addEventListener('mousemove', featureInfoResizeHandler, { passive: true })
  } else if (e instanceof TouchEvent) {
    document.body.addEventListener('touchmove', featureInfoResizeHandler, { passive: true })
  }
}

const featureInfoEndResizeHander = (e) => {
  if (resizing.value) {
    if (e instanceof MouseEvent) {
      document.body.removeEventListener('mousemove', featureInfoResizeHandler, { passive: true })
    } else if (e instanceof TouchEvent) {
      document.body.removeEventListener('touchmove', featureInfoResizeHandler, { passive: true })
    }
    setFeaturePosition()
  }
}

const setFeaturePosition = () => {
  const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
  const x = clamp(Math.round(leftPosition.value), 0, vw - 250)
  const y = clamp(Math.round(bottomPosition.value), 0, vh - height.value)
  const h = clamp(Math.round(height.value), 200, vh - bottomPosition.value)
  setFeatureInfo({
    left: `${x}px`, 
    bottom: `${y}px`,
    height: `${h}px`
  })
}

watch(leftPosition, nv => {
  const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  const x = clamp(Math.round(nv), 0, vw - 250)
  getFeatureInfoElmt.value.style.setProperty(PANEL_LEFT_CSS_VARIABLE_NAME, `${x}px`)
})


watch(bottomPosition, nv => {
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
  const y = clamp(Math.round(nv), 0, vh - height.value)
  getFeatureInfoElmt.value.style.setProperty(PANEL_BOTTOM_CSS_VARIABLE_NAME, `${y}px`)
})

watch(height, nv => {
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
  const h = clamp(Math.round(nv), 200, vh - bottomPosition.value)
  getFeatureInfoElmt.value.style.setProperty(PANEL_HEIGHT_CSS_VARIABLE_NAME, `${h}px`)
})

const getFeatures = async (coordinate, layers, featureToSelect, dispatchEvent = true, forceDisplay = true) => {
  if (isLoading.value) return
  isLoading.value = true

  clear()
  try {
    featureCollection.value = await $map.getRequestModule().getfeatureinfo_request(coordinate, layers)
    isLoading.value = false
    if (!featureCollection.value.features.length) {
      display(false)
      // $map.radialMenu()
      return
    }
    if (featureToSelect) {
      let featureToSelectParam
      if (typeof featureToSelect === 'string') {
        featureToSelectParam = {
          foid: featureToSelect,
          fitOnFeature: true
        }
      } else {
        featureToSelectParam = featureToSelect
      }
      const featureIndexToSelect = featureCollection.value.features.findIndex(feature => feature.properties.foid === featureToSelectParam.foid)
      nextTick(() => {
        if (featureCollectionPanel.value) featureCollectionPanel.value.toggleFeatureProperties(featureCollection.value.features[featureIndexToSelect], featureToSelectParam.fitOnFeature, dispatchEvent)
      })
    } else {
      const sf = autoSelectFeatureIfPossible()
      nextTick(() => {
        if (featureCollectionPanel.value) featureCollectionPanel.value.toggleFeatureProperties(sf, true, dispatchEvent)
      })
    }
  } catch (e) {
    isLoading.value = false
    console.error(e)
  }
  display(forceDisplay || !selectedFeature.value)
}

const autoSelectFeatureIfPossible = () => {
  let particularFeature
  for (const feature of featureCollection.value.features) {
    if (PARTICULAR_FEATURE_TYPES.indexOf(feature.properties.featureType) !== -1) {
      if (particularFeature) {
        particularFeature = undefined
        break
      }
      particularFeature = feature
    }
  }
  return particularFeature
}

const display = (bool) => {
  showInfos.value = bool
  if (!bool) clear()
}

const selectFeature = (detail) => {
  $map.removeLayer(gfiLayer.value)
  nextTick(() => {
    if (detail.selectedFeature) $map.addLayer(gfiLayer.value)
    gfiLayer.value.setGeoJSONSelection(detail.selectedFeature, detail.fitToFeature)
    selectedFeature.value = detail.selectedFeature
  })
}

watch(getLayerColor, () => {
    if (selectedFeature.value) gfiLayer.value.updateSelectedColor()
  }
)
</script>

<template>
  <div ref="getFeatureInfoElmt" class="get-feature-info" :class="{ hide: !showInfos }">
    <div ref="pcBar" class="feature-info-pc-bar">
      <button ref="resizeButton">
        <i class="icon-resize-vertical" />
      </button>
      <div>{{ t('map-objects') }}</div>
      <button @click="closeInfos">
        <i class="icon-cancel" />
      </button>
    </div>
    <generic-loading :show="isLoading" />
    <feature-collection-panel v-if="featureCollection" ref="featureCollectionPanel" :feature-collection="featureCollection" @select-feature="selectFeature" @open-ais="closeFeatureInfo" />
  </div>
</template>

<i18n src="@/locales/getFeatureInfo.json"></i18n>